/** Choosing several people, and how many of them have to act.
 *
 *  ## Why this is not a `<select multiple>`, and not chips in the cell
 *
 *  `PeopleOptions` deliberately used a native `<select>`, and its reasoning was good:
 *  keyboard entry, type-to-find, and the platform picker on a phone, all for free.
 *  Multi-selection gives that up whatever we build - a native multi-select is famously
 *  hostile, needing ctrl-click nobody discovers and showing a scrollbox nobody wants -
 *  so the cost is paid once here and the replacement has to earn it back. Hence the
 *  filter box and full keyboard support below.
 *
 *  Chips rendered *in* the grid cell were the other candidate and are impossible: the
 *  entry table's six fixed columns already total 832px inside a 1080px minimum, and the
 *  Task field was rescued from 68px only recently. A cell that grows with the number of
 *  people would take that back. So the cell shows a summary - "Aarti +2" - and the
 *  choosing happens in a popover, which is the pattern the Documents cell already uses.
 *
 *  ## What it keeps from PeopleOptions
 *
 *  The workload bands, because they are more useful here rather than less: picking three
 *  people makes "who has room" a harder question, not an easier one. The counts come
 *  from the same `['workload']` query, keyed by user id.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { Popover } from './Popover'

import type { Assignee, Workload } from '@/api/client'
import { byName } from './ui'

export type Quorum = 'ANY' | 'ALL'

/** What the closed cell says, in two parts.
 *
 *  Two parts rather than one string because they truncate differently: the name may be
 *  clipped by the 176px cell, and the count may not - losing it would turn three people
 *  into one silently.
 *
 *  The full name is kept and left to CSS to clip. Taking the first word looked tidier
 *  and was wrong on real data: this firm records its owner as "CA Rajesh Mehta", so the
 *  first word is a professional title and the cell read "CA +1".
 */
export function peopleLabel(ids: string[], staff: Assignee[], placeholder = '—') {
  if (!ids.length) return { name: placeholder, more: '' }
  const of = (id: string) => staff.find((p) => p.user_id === id)?.name ?? id
  return { name: of(ids[0]), more: ids.length > 1 ? `+${ids.length - 1}` : '' }
}

/** How the rule reads once it matters. Silent for one person, because with one person
 *  "any of them" and "all of them" are the same sentence and offering the choice would
 *  invite the reader to think it means something. */
export function quorumLabel(ids: string[], rule: Quorum, kind: 'doer' | 'approver') {
  if (ids.length < 2) return ''
  if (kind === 'approver') {
    return rule === 'ALL' ? 'all must approve' : 'any one is enough'
  }
  return rule === 'ALL' ? 'all must finish' : 'any one can finish'
}

type Band = { label: string; people: Assignee[] }

/** The same three bands PeopleOptions used, on the same signals. */
function bands(staff: Assignee[], load: Workload | undefined, kind: 'doer' | 'approver'):
    Band[] {
  const of = (p: Assignee) => load?.by_person?.[p.user_id]
  const byName = (a: Assignee, b: Assignee) => a.name.localeCompare(b.name)
  if (kind === 'approver') {
    const waiting = (p: Assignee) => of(p)?.waiting_to_approve ?? 0
    const free = staff.filter((p) => waiting(p) === 0).sort(byName)
    const busy = staff.filter((p) => waiting(p) > 0)
      .sort((a, b) => waiting(b) - waiting(a))
    return [{ label: 'Nothing waiting', people: free },
            { label: 'Waiting on them', people: busy }].filter((b) => b.people.length)
  }
  const sig = (p: Assignee) => of(p)?.signal ?? ''
  const pick = (...want: string[]) =>
    staff.filter((p) => want.includes(sig(p))).sort(byName)
  const room = pick('free', 'capacity')
  const balanced = pick('balanced')
  const heavy = pick('overloaded')
  const placed = new Set([...room, ...balanced, ...heavy].map((p) => p.user_id))
  const rest = staff.filter((p) => !placed.has(p.user_id)).sort(byName)
  return [{ label: 'Has room', people: room },
          { label: 'Balanced', people: balanced },
          { label: 'Carrying a lot', people: heavy },
          { label: 'Everyone else', people: rest }].filter((b) => b.people.length)
}

function suffix(p: Assignee, load: Workload | undefined, kind: 'doer' | 'approver') {
  const w = load?.by_person?.[p.user_id]
  if (!w) return ''
  if (kind === 'approver') {
    return w.waiting_to_approve ? ` — ${w.waiting_to_approve} waiting` : ''
  }
  return w.open === 0 ? ' — nothing open' : ` — ${w.open} open`
}

export function PeoplePicker({ staff, load, kind, value, rule, onChange, onDone,
                               initialQuery }: {
  staff: Assignee[]
  load: Workload | undefined
  kind: 'doer' | 'approver'
  value: string[]
  rule: Quorum
  onChange: (ids: string[], rule: Quorum) => void
  onDone: () => void
  /** The query to start with, when the picker was opened by typing a letter on the
   *  cell - otherwise that first character is swallowed. */
  initialQuery?: string
}) {
  const [find, setFind] = useState(initialQuery ?? '')
  const box = useRef<HTMLInputElement>(null)
  /** Which row the keyboard is on, counted across ALL the bands rather than within
   *  one: the bands are a way of grouping the same single list, and arrowing down
   *  should walk from the last of "Has room" into the first of "Balanced" without
   *  anybody having to think about the headings. */
  const [at, setAt] = useState(-1)
  const list = useRef<HTMLDivElement>(null)
  const id = useId()

  const matching = useMemo(
    () => byName(staff, find, (p) => p.name), [staff, find])

  const groups = bands(matching, load, kind)
  // The bands flattened back into the one list the arrows move through.
  const flat = groups.flatMap((g) => g.people)
  const toggle = (who: string) => {
    onChange(value.includes(who) ? value.filter((x) => x !== who) : [...value, who],
             rule)
    // Back to the search box after a CLICK, or focus sits on the checkbox and the
    // arrows quietly stop working.
    box.current?.focus()
  }

  // Typing narrows the list, so the old position means nothing - and leaving it put
  // would tick somebody nobody was looking at.
  useEffect(() => { setAt(flat.length ? 0 : -1) }, [find, flat.length])

  useEffect(() => {
    if (at < 0) return
    list.current?.querySelectorAll('[data-option]')[at]
      ?.scrollIntoView({ block: 'nearest' })
  }, [at])

  return (
    <div>
      {/* Type-to-filter, replacing the type-to-find a native select gave for free.
          Autofocused, so the keyboard path is: open, type, Enter. */}
      <input ref={box} className="field text-[13px]" autoFocus value={find}
             // See `CompanyPicker`: the option checkboxes are out of the tab order,
             // so the box has to announce the row the arrows are on.
             role="combobox" aria-expanded aria-controls={`${id}-list`}
             aria-activedescendant={at >= 0 && flat[at]
               ? `${id}-opt-${flat[at].user_id}` : undefined}
             placeholder="Find somebody…"
             onChange={(e) => setFind(e.target.value)}
             onKeyDown={(e) => {
               // Up and down walk the list, across the band headings - see `at`.
               if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                 e.preventDefault()
                 if (!flat.length) return
                 const step = e.key === 'ArrowDown' ? 1 : -1
                 setAt((n) => Math.max(0, Math.min(flat.length - 1, n + step)))
                 return
               }
               // Space ticks whoever is highlighted, and only while the box is
               // empty - people have two names, so once a query is being typed the
               // space bar has to be a space. Arrow-then-space, or type-then-Enter.
               //
               // It does NOT close: ticking three people is the common case here,
               // and closing on the first would make the other two a second act.
               //
               // Read from the INPUT rather than from `find`: the state lags a
               // render behind, so typing "ca r" quickly arrived here with `find`
               // still '' and the space ticked somebody instead of separating the
               // words.
               if (e.key === ' ' && !e.currentTarget.value.trim()) {
                 if (at < 0 || !flat[at]) return
                 e.preventDefault()
                 toggle(flat[at].user_id)
                 return
               }
               if (e.key === 'Enter') {
                 e.preventDefault()
                 // Whoever the keyboard is on, falling back to the only remaining
                 // match so type-three-letters-and-Enter still works untouched.
                 const one = at >= 0 ? flat[at] : flat.length === 1 ? flat[0] : null
                 if (one) { toggle(one.user_id); setFind('') }
                 else if (!flat.length) onDone()
                 return
               }
               if (e.key === 'Escape') { e.preventDefault(); onDone() }
             }} />

      <div ref={list} id={`${id}-list`} role="listbox"
           className="mt-2 max-h-[15rem] overflow-auto">
        {groups.length === 0 ? (
          <p className="text-[12px] text-ink-3 px-1 py-2">Nobody matches that.</p>
        ) : groups.map((g) => (
          <div key={g.label} className="mb-1.5">
            <div className="text-[10.5px] uppercase tracking-[0.07em] text-ink-3
                            px-1 pb-0.5">{g.label}</div>
            {g.people.map((p) => (
              <label key={p.user_id} data-option
                     id={`${id}-opt-${p.user_id}`} role="option"
                     aria-selected={value.includes(p.user_id)}
                     // The flat position, so the highlight and the arrows agree
                     // across the band headings.
                     onMouseEnter={() => setAt(
                       flat.findIndex((x) => x.user_id === p.user_id))}
                     className={`flex items-center gap-2 px-1 py-1 rounded
                                cursor-pointer text-[12.5px] ${
                       flat[at]?.user_id === p.user_id
                         ? 'bg-primary-tint ring-1 ring-inset ring-primary/40' : ''}`}>
                {/* Out of the tab order on purpose - the arrows and space are the way
                    through the list, which leaves Tab to reach the quorum radios and
                    Done in two presses rather than one per person. */}
                <input type="checkbox" tabIndex={-1}
                       checked={value.includes(p.user_id)}
                       onChange={() => toggle(p.user_id)} />
                <span className="truncate">{p.name}</span>
                {/* Marked, never removed. Somebody who has signed off is still a
                    legitimate choice - the server asks rather than refusing - and
                    hiding the name would take away the decision. A manager needs it
                    most of all, because he is the person who can reopen her day. */}
                {p.day_closed && (
                  <span className="text-[10.5px] px-1.5 py-px rounded bg-pend-tint
                                   text-pend-ink whitespace-nowrap"
                        title={p.day_remarks || 'Signed off for the day'}>
                    signed off
                  </span>
                )}
                {/* Marked the same way and for the same reason: they may still be the
                    right person, and whoever is assigning can walk over and tell them.
                    What must not happen is finding out afterwards - which is how a
                    task ended up waiting on an approver who had never started the bot
                    and never knew it existed. The reason is the server's own sentence,
                    so this and Master data cannot disagree about one person. */}
                {p.notifiable === false && (
                  <span className="text-[10.5px] px-1.5 py-px rounded bg-rej-tint
                                   text-rej-ink whitespace-nowrap"
                        title={p.why_not_notifiable || 'Will not be told'}>
                    not told
                  </span>
                )}
                <span className="ml-auto text-[11px] text-ink-3 whitespace-nowrap">
                  {suffix(p, load, kind)}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>

      {/* The rule, and only once it can mean anything. Appearing on the second pick is
          the point: a one-person task never shows a control whose two options are the
          same thing. */}
      {value.length > 1 && (
        <div className="mt-2 pt-2 border-t border-rule">
          {(['ANY', 'ALL'] as Quorum[]).map((r) => (
            <label key={r} className="flex items-center gap-2 py-0.5 text-[12.5px]">
              {/* A shared `name` makes these one radio GROUP: one tab stop instead of
                  two, and the arrows switch the rule, which is what anyone tries. */}
              <input type="radio" name={`${id}-rule`} checked={rule === r}
                     onChange={() => onChange(value, r)} />
              {quorumLabel(value, r, kind)}
            </label>
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2">
        <span className="text-[11.5px] text-ink-3">
          {value.length} selected
        </span>
        <span className="flex-1" />
        <button type="button" className="btn btn-sm btn-primary" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  )
}

/** One grid cell: the summary, and the picker in a popover over it.
 *
 *  Separate from `PeoplePicker` because the drawer and the edit form want the picker
 *  without a cell around it, and the grid wants the cell. The popover geometry is the
 *  same as the Documents cell's, deliberately - two different popovers in one row would
 *  be two things to learn.
 */
export function PersonCell({ open, onOpen, staff, load, kind, ids, rule, onChange,
                             placeholder = '—', onExit, label: cellLabel,
                             initialQuery }: {
  open: boolean
  onOpen: (v: boolean) => void
  /** Tab has run off the end of the panel - see `Popover`. */
  onExit?: (dir: 'forward' | 'back') => void
  /** Seeded when the picker was opened by typing a letter on the cell. */
  initialQuery?: string
  /** What this cell is, for a screen reader. Without it the doer and the approver
   *  cell in one row announce identically - both are just their own truncated text. */
  label?: string
  staff: Assignee[]
  load: Workload | undefined
  kind: 'doer' | 'approver'
  ids: string[]
  rule: Quorum
  onChange: (ids: string[], rule: Quorum) => void
  placeholder?: string
}) {
  const rules = quorumLabel(ids, rule, kind)
  const label = peopleLabel(ids, staff, placeholder)
  const trigger = useRef<HTMLButtonElement>(null)
  return (
    <div className="relative">
      <button ref={trigger} type="button"
              className="field flex items-center gap-1 text-left w-full"
              aria-label={cellLabel} aria-haspopup="listbox" aria-expanded={open}
              onClick={() => onOpen(!open)}>
        <span className={ids.length ? 'truncate' : 'truncate text-ink-3'}>
          {label.name}
        </span>
        {/* shrink-0: the name may be clipped, the count may not - a clipped "+2" would
            turn three people into one without saying so. */}
        {label.more && <span className="shrink-0 text-ink-2">{label.more}</span>}
        <span className="ml-auto text-ink-3 text-[10px]">▾</span>
      </button>
      {/* The rule under the cell, not inside it: the cell has 176px and a name already
          fills it, and this is the fact somebody scanning the grid needs to see without
          opening anything. */}
      {rules && (
        <span className="block text-[10.5px] text-ink-3 mt-0.5 truncate">{rules}</span>
      )}

      {/* Portalled: the entry grid's scroll box would otherwise clip this on the
          last rows, which is where it is opened most. `PeoplePicker` itself is
          untouched - the drawer and the edit form render it inline. */}
      <Popover anchor={trigger.current} open={open} onClose={() => onOpen(false)}
               onExit={onExit}>
        <div className="p-3">
          <PeoplePicker staff={staff} load={load} kind={kind} value={ids} rule={rule}
                        onChange={onChange} onDone={() => onOpen(false)}
                        initialQuery={initialQuery} />
        </div>
      </Popover>
    </div>
  )
}

/** One task's approval progress, in words, or '' when there is nothing worth saying.
 *
 *  Shared, because the task table, the drawer, the approvals queue and Today all have to
 *  say the same thing about the same task - and four copies of "count the approved ones"
 *  would eventually disagree about whether a rejection counts towards the total.
 *
 *  Silent for a single approver: "1 of 1 approved" is noise, and the status chip already
 *  says Approved. It speaks only when the count is a fact somebody needs.
 */
export function approvalProgress(t: {
  people?: { kind: string; decision: string }[]
  approver_rule?: string
  status?: string
}): string {
  const approvers = (t.people ?? []).filter((p) => p.kind === 'APPROVER')
  if (approvers.length < 2) return ''
  if (t.status !== 'PENDING_APPROVAL') return ''
  const yes = approvers.filter((p) => p.decision === 'APPROVED').length
  // Under ANY one approval is enough, so "1 of 3 approved" would imply two more are
  // needed when they are not. What matters there is only that nobody has decided yet.
  if ((t.approver_rule ?? 'ANY') === 'ANY') {
    return yes ? '' : `waiting on any of ${approvers.length}`
  }
  return `${yes} of ${approvers.length} approved`
}

/** The task's other approvers, by name, for "· with Asha and Ravi".
 *
 *  Named one by one rather than taken from the joined `approver` string, and matched
 *  against every approver rather than the `approver_user_id` mirror - that column is
 *  the first of them and nothing more, so the second approver on a task was told it
 *  was "with" a list they were themselves in.
 *
 *  Empty when the viewer is the only approver, which is what keeps a queue of your own
 *  rows from repeating your name down the page.
 */
export function othersApproving(t: {
  people?: { user_id: string; kind: string; name: string }[]
  approver?: string
  approver_user_id?: string
}, userId: string | undefined): string[] {
  const approvers = (t.people ?? []).filter((p) => p.kind === 'APPROVER')
  if (approvers.length) {
    return approvers.filter((p) => p.user_id !== userId).map((p) => p.name)
  }
  // Rows filed before `people` existed carry one approver in the singular fields.
  if (!t.approver_user_id || t.approver_user_id === userId) return []
  return t.approver ? [t.approver] : []
}

/** Whether this viewer still has a decision to give on this task.
 *
 *  Drives whether the approve buttons appear: being one of the approvers is not the same
 *  as still being asked, and showing the buttons to somebody who has already decided
 *  invites them to decide twice.
 */
export function awaitingMyDecision(t: {
  people?: { user_id: string; kind: string; decision: string }[]
}, userId: string | undefined): boolean {
  if (!userId) return false
  return (t.people ?? []).some(
    (p) => p.kind === 'APPROVER' && p.user_id === userId && !p.decision)
}
